import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Box, Stack } from '@mui/material';
import { registerUnauthorizedHandler } from '../api/client';
import { setAccessToken } from '../api/tokenStore';
import { loginRequest, logoutRequest, meRequest, refreshRequest } from '../api/auth';
import { CurrentUser } from '../types/auth';
import { Logo } from '../components/Logo';
import { userHasPermission } from '../utils/permissions';
import { AuthContext, AuthContextValue } from './authContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    const profile = await meRequest();
    setUser(profile);
    return profile;
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(clearSession);
    return () => registerUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    (async () => {
      try {
        const { accessToken } = await refreshRequest();
        setAccessToken(accessToken);
        await refreshProfile();
      } catch {
        clearSession();
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { accessToken } = await loginRequest(email, password);
    setAccessToken(accessToken);
    // Returns the freshly-fetched profile (not just void) so the login
    // page can validate a post-login redirect against it directly —
    // useAuth()'s own hasPermission below is a stale closure at the
    // moment this resolves (it reflects the pre-login, logged-out user
    // until LoginPage re-renders), so callers that need to check
    // permissions on the just-logged-in user must use the returned value,
    // not this context's hasPermission, until the next render.
    return refreshProfile();
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const hasPermission = useCallback((module: string, action: string) => userHasPermission(user, module, action), [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshProfile,
      hasPermission,
    }),
    [user, isLoading, login, logout, refreshProfile, hasPermission],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Stack spacing={3} alignItems="center">
          <Logo height={48} />
          <CircularProgress size={28} thickness={4} color="secondary" />
        </Stack>
      </Box>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
